import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../dto/data-model.js';

export enum NTAKProgramMainCategory {
  PublicBath = 'KOZFURDO',
  NaturalBath = 'TERMESZETES_FURDO',
  MuseumFortressCastle = 'MUZEUM_VAR_KASTELY',
  TouristTransport = 'TURISZTIKAI_KOZLEKEDES',
  AdventurePark = 'KALANDPARK',
  Zoo = 'ALLATKERT',
  AmusementPark = 'VIDAMPARK',
  NaturalReserve = 'VEDETT_TERMESZETI_TERULET',
  MusicDanceEvent = 'ZENES_TANCOS_RENDEZVENY',
}

export enum NTAKProgramSubCategory {
  // Allowed for `MuseumFortressCastle` and `NaturalReserve` main categories.
  GuidedTour = 'TARLATVEZETES',
  EducationalProgram = 'OKTATASI_PROGRAM',

  // Allowed only for `PublicBath` main category.
  Bath = 'FURDO_PROGRAM',
  Beach = 'STRAND_PROGRAM',
  SwimmingPool = 'USZODAI_PROGRAM',
  Wellness = 'WELLNESS_PROGRAM',
  Health = 'EGESZSEGUGYI_SZOLGALTATAS',

  // Allowed only for `NaturalBath` main category.
  OpenWaterBeach = 'SZABADVIZI_STRAND_PROGRAM',

  // Allowed only for `MuseumFortressCastle` main category.
  Exhibition = 'KIALLITAS',
  MuseumPedagogy = 'MUZEUMPEDAGOGIA',
  ChurchEstablishmentVisit = 'EGYHAZI_LETESITMENY_LATOGATASA',
  CastleVisit = 'KASTELYLATOGATAS',
  FortressVisit = 'VARLATOGATAS',
  OtherMonumentVisit = 'EGYEB_MUEMLEK_LATOGATASA',

  // Allowed only for `TouristTransport` main category.
  LakeBoating = 'TAVI_HAJOZAS',
  RiverBoating = 'FOLYAMI_HAJOZAS',
  HopOnHopOff = 'HOP_ON_HOP_OFF',

  // Allowed only for `AdventurePark` main category.
  AdventureParkProgram = 'KALANDPARKI_PROGRAM',
  ExperienceCenterProgram = 'ELMENYKOZPONTI_PROGRAM',

  // Allowed only for `Zoo` main category.
  ZooProgram = 'ALLATKERTI_PROGRAM',
  WildlifeParkProgram = 'VADASPARKI_PROGRAM',

  // Allowed only for `AmusementPark` main category.
  AmusementParkProgram = 'VIDAMPARKI_PROGRAM',

  // Allowed only for `NaturalReserve` main category.
  NationalParkVisit = 'NEMZETI_PARK_LATOGATAS',
  CaveVisit = 'BARLANGLATOGATAS',
  ParkVisit = 'PARKLATOGATAS',
  ThematicExhibitionVisit = 'TEMATIKUS_BEMUTATOHELY_LATOGATAS',
  ToursTrips = 'TURAK_KIRANDULASOK',

  // Allowed only for `MusicDanceEvent` main category.
  CulturalEvent = 'KULTURALIS_RENDEZVENY',
  Concert = 'KONCERT',
  Festival = 'FESZTIVAL',
}

export enum NTAKProgramFrequency {
  Constant = 'ALLANDO',
  OneTime = 'EGYSZERI',
  FewOccasions = 'NEHANY_ALKALMAS',
  Intermittent = 'IDOSZAKOS',
  SystematicallyRepeating = 'RENDSZERES_ISMETLODO',
}

export enum NTAKProgramType {
  Standalone = 'ONALLO_PROGRAM',
  PartOfASeries = 'PROGRAMSOROZAT_RESZE',
  Combined = 'KOMBINALT_PROGRAM',
}

/**
 * @deprecated this info is now directly stored in the {@link NTAKEventSettingsComponent} model.
 */
export class NTAKProgramSettings extends DataModel {
  static readonly TYPE_ID: string = 'ca3aacd982b44ae9a1ac3bd076c1125f';

  private constructor(
    public readonly mainCategory: NTAKProgramMainCategory,
    public readonly subCategory: NTAKProgramSubCategory,
    public readonly frequency: NTAKProgramFrequency,
    public readonly type: NTAKProgramType,
    public readonly nameOfSeries: string | null,
    public readonly canonicalId: string | null,
  ) {
    super();
  }

  get $typeId(): string {
    return NTAKProgramSettings.TYPE_ID;
  }

  static create(args: {
    mainCategory: NTAKProgramMainCategory,
    subCategory: NTAKProgramSubCategory,
    frequency: NTAKProgramFrequency,
    type: NTAKProgramType,
    nameOfSeries: string | null,
    canonicalId: string | null,
  }): NTAKProgramSettings {
    return new NTAKProgramSettings(
        args.mainCategory,
        args.subCategory,
        args.frequency,
        args.type,
        args.nameOfSeries,
        args.canonicalId,
    );
  }

  static fromMap(
      context: SerializationContext,
      map: SerializedData
  ): NTAKProgramSettings {
    return new NTAKProgramSettings(
        toEnumFromString(MapUtils.get<string>(map, 'mainCategory'), NTAKProgramMainCategory),
        toEnumFromString(MapUtils.get<string>(map, 'subCategory'), NTAKProgramSubCategory),
        toEnumFromString(MapUtils.get<string>(map, 'frequency'), NTAKProgramFrequency),
        toEnumFromString(MapUtils.get<string>(map, 'type'), NTAKProgramType),
        MapUtils.getOrNull<string>(map, 'nameOfSeries'),
        MapUtils.getOrNull<string>(map, 'canonicalId'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      mainCategory: fromEnumAsString(this.mainCategory),
      subCategory: fromEnumAsString(this.subCategory),
      frequency: fromEnumAsString(this.frequency),
      type: fromEnumAsString(this.type),
      nameOfSeries: this.nameOfSeries,
      canonicalId: this.canonicalId,
    };
  }

  clone(): this {
    return new NTAKProgramSettings(
        this.mainCategory,
        this.subCategory,
        this.frequency,
        this.type,
        this.nameOfSeries,
        this.canonicalId,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.mainCategory === this.mainCategory &&
        other.subCategory === this.subCategory &&
        other.frequency === this.frequency &&
        other.type === this.type &&
        other.nameOfSeries === this.nameOfSeries &&
        other.canonicalId === this.canonicalId;
  }
}

export class NTAKProgramSettingsBuilder extends DataModelBuilder<NTAKProgramSettings> {
  build(context: SerializationContext, map: SerializedData): NTAKProgramSettings {
    return NTAKProgramSettings.fromMap(context, map);
  }
}
